import React, { useState, useEffect } from "react";
import { NumericFormat } from 'react-number-format';
import "./FastExchange.css";
import CurrencyExchangerService from "../../services/CurrencyExchangerService";
import CurrencySelect from "../ExchangeBLock/CurrencySelect/CurrencySelect";
import CountrySelect from "../ExchangeBLock/CountrySelect/CountrySelect";
import {ExchangeRequestService} from "../../services/ExchangeRequestService";
import {useExchange} from "../../Context/Exchange";
import {useNavigate} from "react-router-dom";
import {getCurrencyConfig, getDecimalScale} from "../utils/getDecimalScale";

const currencies = ["USDT", "BTC", "ETH", "USD", "EUR", "ILS", "MDL", "CZK", "GBP"];

const FastExchange = () => {
    const {
        fromValue, setFromValue,
        fromCurrency, setFromCurrency,
        toValue, setToValue,
        toCurrency, setToCurrency,
        exchangeRate, setExchangeRate,
        selectedCountry, setSelectedCountry,
        reserve,
        minAmount
    } = useExchange();
    const [accountOwner, setAccountOwner] = useState("");
    const [bankName, setBankName] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [walletAddress, setWalletAddress] = useState("");
    const isFiatCurrency = ["USD", "EUR", "ILS", "MDL", "CZK", "GBP"].includes(toCurrency);
    const isCryptoCurrency = ["USDT", "BTC", "ETH"].includes(toCurrency);
    const navigate = useNavigate();

    useEffect(() => {
        fetchExchangeRate();
    }, [fromCurrency, toCurrency]);

    const fetchExchangeRate = async () => {
        try {
            const data = await CurrencyExchangerService.fetchCurrencyData(fromCurrency.toLowerCase());

            if (data && data[toCurrency.toLowerCase()]) {
                const rate = data[toCurrency.toLowerCase()];
                setExchangeRate(rate);

                const numericFromValue = parseFloat(fromValue.replace(/,/g, '')) || 0;
                const newToValue = (numericFromValue * rate);
                setToValue(newToValue);
            } else {
                setExchangeRate(1);
                setToValue(fromValue.replace(/,/g, ''));
            }
        } catch (error) {
            console.error("Error fetching exchange rate", error);
            setExchangeRate(1);
            setToValue(fromValue.replace(/,/g, ''));
        }
    };

    const handleExchangeRequest = async () => {
        const giveAmount = parseFloat(fromValue.replace(/,/g, '')) || 0;
        const receiveAmount = typeof toValue === 'string'
            ? parseFloat(toValue.replace(/,/g, '')) || 0
            : toValue;

        const exchangeRequestDto = {
            giveCurrency: fromCurrency,
            giveAmount: giveAmount,
            rate: exchangeRate.toFixed(6),
            receiveCurrency: toCurrency,
            receiveAmount: receiveAmount.toFixed(6),
            country: selectedCountry,
        };

        if (isFiatCurrency) {
            exchangeRequestDto.ownerName = accountOwner;
            exchangeRequestDto.bankName = bankName;
            exchangeRequestDto.accountNumber = accountNumber;
            exchangeRequestDto.walletAddress = null;
        } else if (isCryptoCurrency) {
            exchangeRequestDto.walletAddress = walletAddress;
            exchangeRequestDto.ownerName = null;
            exchangeRequestDto.bankName = null;
            exchangeRequestDto.accountNumber = null;
        }

        try {
            const response = await ExchangeRequestService.createExchangeRequest(exchangeRequestDto);
            navigate('/exchange-result', { state: { requestNumber: response.requestNumber } });
        } catch (error) {
            console.error("Error creating exchange request:", error);
        }
    };

    const handleFromValueChange = (values) => {
        const { value } = values;
        setFromValue(value);

        if (exchangeRate) {
            const numericValue = parseFloat(value) || 0;
            const convertedValue = (numericValue * exchangeRate);
            setToValue(convertedValue);
        }
    };

    const isButtonDisabled = () => {
        const numericFromValue = parseFloat(fromValue.replace(/,/g, '')) || 0;

        if (!fromValue || !toValue || !fromCurrency || !toCurrency || !selectedCountry || numericFromValue < minAmount) {
            return true;
        }

        if (isFiatCurrency && (!accountOwner || !bankName || !accountNumber)) {
            return true;
        }

        if (isCryptoCurrency && !walletAddress) {
            return true;
        }

        return false;
    };

    return (
        <div className="fast-exchange-container">
            <h3>Быстрый обмен</h3>
            <form className="fast-exchange-form">
                <div className="input-block">
                    <div className="input-from">
                        <label>Отдаете</label>
                        <NumericFormat
                            value={fromValue}
                            onValueChange={handleFromValueChange}
                            thousandSeparator={true}
                            decimalScale={getDecimalScale(fromCurrency)}
                            valueIsNumericString={true}
                            allowNegative={false}
                            className="number-format-input"
                            isAllowed={(values) => {
                                const { value } = values;
                                if (value.startsWith('0') && value.length > 1 && !value.startsWith('0.')) {
                                    return false;
                                }
                                return true;
                            }}
                        />
                    </div>
                    <CurrencySelect
                        selectedCurrency={fromCurrency}
                        onCurrencyChange={setFromCurrency}
                        currencies={currencies}
                    />
                </div>

                <p className="min-amount">
                    Минимальная сумма перевода
                    <NumericFormat
                        value={getCurrencyConfig(fromCurrency).minAmountFormat(minAmount)}
                        thousandSeparator={true}
                        decimalScale={getCurrencyConfig(fromCurrency).decimalScale}
                        displayType="text"
                    /> {fromCurrency}
                </p>

                <div className="rate-info">
                    <span>
                        1 {fromCurrency} = {exchangeRate?.toFixed(6)} {toCurrency}
                        <strong style={{ color: "#eecb12", fontSize: "10px" }}> фиксированный</strong>
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="30px" height="30px" viewBox="0 0 24 24" fill="#9F9F9F"><path d="M14 9v2h-4v-2c0-1.104.897-2 2-2s2 .896 2 2zm10 3c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12zm-8-1h-1v-2c0-1.656-1.343-3-3-3s-3 1.344-3 3v2h-1v6h8v-6z"></path></svg>
                </div>

                <div className="input-block">
                    <div className="input-to">
                        <label>Получаете</label>
                        <NumericFormat
                            readOnly={true}
                            value={toValue}
                            thousandSeparator={true}
                            decimalScale={getDecimalScale(toCurrency)}
                            className="number-format-input"
                        />
                    </div>
                    <CurrencySelect
                        selectedCurrency={toCurrency}
                        onCurrencyChange={setToCurrency}
                        currencies={currencies}
                    />
                </div>

                <p className="reserve-amount">
                    Наш резерв: {reserve.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {toCurrency}
                </p>

                <CountrySelect
                    setSelectedCountry={setSelectedCountry}
                    selectedCountry={selectedCountry}
                />

                {isFiatCurrency && (
                    <>
                        <div className="fiat-currency-input">
                            <div className="fiat-currency-input-field">
                                <label>Имя владельца</label>
                                <input
                                    type="text"
                                    value={accountOwner}
                                    onChange={(e) => setAccountOwner(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="fiat-currency-input">
                            <div className="fiat-currency-input-field">
                                <label>Название банка</label>
                                <input
                                    type="text"
                                    value={bankName}
                                    onChange={(e) => setBankName(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="fiat-currency-input">
                            <div className="fiat-currency-input-field">
                                <label>Номер Счёта</label>
                                <input
                                    type="text"
                                    value={accountNumber}
                                    onChange={(e) => setAccountNumber(e.target.value)}
                                />
                            </div>
                        </div>
                    </>
                )}

                {isCryptoCurrency && (
                    <div className="fiat-currency-input">
                        <div className="fiat-currency-input-field">
                            <label>Адрес кошелька</label>
                            <input
                                type="text"
                                value={walletAddress}
                                onChange={(e) => setWalletAddress(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                <button
                    type="button"
                    className="fast-exchange-exchange-button"
                    onClick={handleExchangeRequest}
                    disabled={isButtonDisabled()}
                >
                    Продолжить
                </button>
            </form>
        </div>
    );
};

export default FastExchange;